import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowUpRight, Clock } from "lucide-react";
import { listPublishedPosts } from "@/lib/blog-public.functions";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { BLOG_TOPICS, findBlogTopic, topicHaystack } from "@/data/blog-topics";
import { imgSrcSet, CARD_SIZES } from "@/lib/img";
import { PageHeader } from "./fordon";

const BASE = "https://www.akhusbilfritid.se";

type PostCard = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_description: string | null;
  cover_url: string | null;
  published_at: string | null;
  reading_minutes: number | null;
  keywords: string[] | null;
  focus_keyword: string | null;
};

export const Route = createFileRoute("/blogg/amne/$topic")({
  loader: async ({ params }) => {
    const topic = findBlogTopic(params.topic);
    if (!topic) throw notFound();
    const { posts } = await listPublishedPosts();
    const all = (posts ?? []) as PostCard[];
    const matched = all.filter((p) => topic.re.test(topicHaystack(p)));
    return { topicId: topic.id, posts: matched };
  },
  head: ({ params, loaderData }) => {
    const topic = findBlogTopic(params.topic);
    const url = `${BASE}/blogg/amne/${params.topic}`;
    if (!topic || !loaderData) {
      return { meta: [{ title: "Ämnet finns inte" }, { name: "robots", content: "noindex" }] };
    }
    const count = loaderData.posts.length;
    const title = `${topic.label} – ${count} guider | AK Husbil & Fritid`;
    return {
      meta: [
        { title },
        { name: "description", content: topic.description },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: topic.description },
        { property: "og:url", content: url },
        { property: "og:locale", content: "sv_SE" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(loaderData.posts[0]?.cover_url
          ? [
              { property: "og:image", content: loaderData.posts[0].cover_url as string },
              { name: "twitter:image", content: loaderData.posts[0].cover_url as string },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "CollectionPage",
                name: topic.heading,
                description: topic.description,
                url,
                inLanguage: "sv-SE",
                isPartOf: { "@type": "Blog", "@id": `${BASE}/blogg#blog` },
              },
              {
                "@type": "ItemList",
                numberOfItems: loaderData.posts.length,
                itemListElement: loaderData.posts.slice(0, 30).map((p, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  url: `${BASE}/blogg/${p.slug}`,
                  name: p.title,
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Hem", item: `${BASE}/` },
                  { "@type": "ListItem", position: 2, name: "Blogg", item: `${BASE}/blogg` },
                  { "@type": "ListItem", position: 3, name: topic.label, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: TopicPage,
  notFoundComponent: () => (
    <div className="container-edge py-32 text-center">
      <h1 className="font-display text-3xl">Ämnet finns inte</h1>
      <Link to="/blogg" className="mt-4 inline-block text-accent hover:underline">
        ← Till bloggen
      </Link>
    </div>
  ),
});

function TopicPage() {
  const { topicId, posts: rawPosts } = Route.useLoaderData();
  const posts = rawPosts as PostCard[];
  const topic = findBlogTopic(topicId)!;
  const totalMinutes = posts.reduce((n: number, p: PostCard) => n + (p.reading_minutes ?? 6), 0);

  return (
    <>
      <PageHeader
        label={`Blogg · ${topic.label}`}
        title={
          <>
            {topic.heading.split(" ")[0]}{" "}
            <span className="font-italic-display text-accent">
              {topic.heading.split(" ").slice(1).join(" ")}
            </span>
          </>
        }
        sub={topic.intro}
      />

      <section className="container-edge pt-10 md:pt-14">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-wider text-muted-foreground">
          <span className="text-accent">{posts.length} artiklar</span>
          <span className="inline-flex items-center gap-2">
            <Clock size={13} /> ~{totalMinutes} min läsning
          </span>
        </div>

        <div className="-mx-4 mt-6 overflow-x-auto px-4 pb-1 md:mx-0 md:px-0">
          <div className="flex w-max gap-2 md:w-auto md:flex-wrap">
            <Link
              to="/blogg"
              className="shrink-0 rounded-full border border-border bg-card px-4 py-2 text-sm transition hover:bg-secondary"
            >
              Alla artiklar
            </Link>
            {BLOG_TOPICS.map((t) => (
              <Link
                key={t.id}
                to="/blogg/amne/$topic"
                params={{ topic: t.id }}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm transition ${
                  t.id === topic.id
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card hover:bg-secondary"
                }`}
              >
                {t.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container-edge py-12 md:py-16">
        {posts.length === 0 ? (
          <div className="rounded-3xl border border-border bg-card p-10 text-center">
            <h2 className="font-display text-2xl md:text-3xl">Inga artiklar i ämnet ännu.</h2>
            <Link to="/blogg" className="mt-5 inline-block text-accent hover:underline">
              Se alla artiklar →
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <Link
                key={p.id}
                to="/blogg/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card hover-lift"
              >
                {p.cover_url ? (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_url}
                      srcSet={imgSrcSet(p.cover_url)}
                      sizes={CARD_SIZES}
                      alt={p.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] bg-secondary" />
                )}
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-medium uppercase tracking-wider text-accent">
                      {p.published_at ? new Date(p.published_at).toLocaleDateString("sv-SE") : "Guide"}
                      {p.reading_minutes ? ` · ${p.reading_minutes} min` : ""}
                    </p>
                    <ArrowUpRight size={18} className="shrink-0 text-muted-foreground" />
                  </div>
                  <h2 className="mt-3 font-display text-2xl leading-tight">{p.title}</h2>
                  {(p.excerpt || p.meta_description) && (
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {p.excerpt ?? p.meta_description}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16">
          <NewsletterSignup
            variant="inline"
            source={`blog-topic-${topic.id}`}
            title="Nya guider direkt i inkorgen"
            description="Få våra bästa husbils- och husvagnsguider plus nya fordon i lager. ~1 mejl/månad."
          />
        </div>
      </section>
    </>
  );
}
